// components/ExpirationDateButton.tsx
'use client';
import { useEffect, useState, useRef } from 'react';
import ExpirationDateTooltip from './ExpirationDateModal';

interface ExpirationDateButtonProps {
    sku: string;
    productName: string;
    productImage: string;
}

interface ProductApiResponse {
    result: {
        data: {
            results: Array<{
                id: string;
                name: string;
                vendorCode: string;
                properties: {
                    collection?: {
                        value: string;
                        captionRu: string;
                        captionEn: string;
                    };
                };
                officeName: string;
            }>;
        };
    };
}

export default function ExpirationDateButton({ sku, productName, productImage }: ExpirationDateButtonProps) {
    const [expirationDate, setExpirationDate] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const fetchExpirationDate = async () => {
            try {
                // Используем наш серверный API route
                const response = await fetch('/api/stock', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ sku }),
                });

                const responseData: ProductApiResponse = await response.json();

                if (!response.ok || !responseData.result?.data?.results?.length) {
                    throw new Error('Товар не найден');
                }

                const results = responseData.result.data.results;
                const productWithExpiration = results.find((product: any) =>
                    product.properties?.collection?.value
                );

                if (productWithExpiration?.properties?.collection?.value) {
                    const dateValue = productWithExpiration.properties.collection.value;
                    const date = new Date(dateValue);

                    if (!isNaN(date.getTime())) {
                        const formattedDate = date.toLocaleDateString('ru-RU', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        });
                        setExpirationDate(formattedDate);
                    } else {
                        throw new Error('Неверный формат даты');
                    }
                } else {
                    throw new Error('Срок годности не указан');
                }
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Ошибка при получении срока годности');
            } finally {
                setLoading(false);
            }
        };

        fetchExpirationDate();
    }, [sku]);

    const toggleTooltip = () => {
        setIsTooltipOpen(!isTooltipOpen);
    };

    const closeTooltip = () => {
        setIsTooltipOpen(false);
    };

    // Показываем кнопку только когда данные загружены и есть дата
    if (loading || error || !expirationDate) {
        return null;
    }

    return (
        <>
            <button
                ref={buttonRef}
                onClick={toggleTooltip}
                style={{
                    position: 'absolute',
                    bottom: '15px',
                    left: '10px',
                    backgroundColor: '#ff953578',
                    color: 'black',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '5px 10px',
                    fontSize: '14px',
                    fontWeight: '300',
                    cursor: 'pointer',
                    zIndex: 10,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                    transition: 'all 0.1s ease',
                    backdropFilter: 'blur(8px)',
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    animation: 'slideInFade 0.3s ease-out 0.1s forwards'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff9535';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(-12px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff953578';
                    e.currentTarget.style.color = '#000000';
                    e.currentTarget.style.transform = 'translateY(-10px)';
                }}
            >
                Срок годности
            </button>

            <ExpirationDateTooltip
                isOpen={isTooltipOpen}
                onClose={closeTooltip}
                expirationDate={expirationDate}
                buttonRef={buttonRef}
            />

            <style jsx>{`
                @keyframes slideInFade {
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </>
    );
}